import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { arrowUpRightMask } from '../ui/icons/ArrowUpRight';

const Trace = styled.svg`
  display: block;
  width: 100%;
  height: 66px;
  overflow: visible;
  color: var(--brass);
  .signal {
    stroke-dasharray: 135 1100;
    animation: signal-travel 8s linear infinite;
  }
  @keyframes signal-travel {
    from {
      stroke-dashoffset: 1235;
    }
    to {
      stroke-dashoffset: 0;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .signal {
      animation: none;
      stroke-dasharray: none;
    }
  }
`;
export function PulseTrace() {
  const path =
    'M0 33 H340 L360 25 L378 42 L398 8 L417 58 L436 27 L451 33 H1200';
  return (
    <Trace viewBox="0 0 1200 66" preserveAspectRatio="none" aria-hidden="true">
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        opacity=".22"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
      <path
        className="signal"
        d={path}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
    </Trace>
  );
}
export const BrandKicker = styled.p`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 28px;
  font: 500 1.1rem/1.5 var(--font-mono);
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--muted);
  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--brass);
    flex-shrink: 0;
  }
`;
const linkStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 36px;
  padding: 16px 0;
  border-bottom: 1px solid var(--ink);
  text-decoration: none;
  font-size: 1.4rem;
  min-height: 48px;
  &::after {
    ${arrowUpRightMask}
    font-size: 2.8rem;
    line-height: 1;
    transition: transform 0.25s;
  }
  &:hover::after {
    transform: translate(3px, -3px);
  }
`;
export const BrandLink = styled(Link)`
  ${linkStyle}
`;
export const BrandAnchor = styled.a`
  ${linkStyle}
`;
const Manifesto = styled.section`
  padding: clamp(64px, 8vw, 120px) 0;
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(0, 1fr);
  gap: 48px;
  align-items: end;
  border-top: 1px solid var(--ink);
  h2 {
    margin: 0;
    font-size: clamp(4.6rem, 7.5vw, 10.8rem);
    font-weight: 650;
    line-height: 0.96;
    letter-spacing: -0.075em;
    text-transform: uppercase;
  }
  h2 span {
    display: block;
    color: var(--muted);
  }
  p {
    max-width: 380px;
    color: var(--muted);
    font-size: 1.8rem;
  }
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 24px;
    h2 {
      font-size: clamp(4rem, 10.5vw, 7rem);
    }
  }
`;
export function Conviction() {
  const { i18n } = useTranslation();
  const fr = i18n.language.startsWith('fr');
  return (
    <Manifesto>
      <div>
        <BrandKicker>{fr ? 'Notre moteur' : 'What drives us'}</BrandKicker>
        <h2>
          {fr ? 'Voir grand' : 'Think bold'}
          <span>{fr ? 'Construire juste' : 'Build with care'}</span>
        </h2>
      </div>
      <div>
        <p>
          {fr
            ? 'Voir grand exige plus qu’une idée forte. Il faut comprendre comment le travail se fait, prendre les décisions difficiles et rester jusqu’à ce que le changement tienne.'
            : 'Thinking big takes more than a strong idea. We learn how the work gets done, make the difficult calls, and stay until the change holds.'}
        </p>
        <BrandLink to={fr ? '/fr/a-propos' : '/en/about'}>
          {fr ? 'Ce qui nous anime' : 'Meet the minds behind the work'}
        </BrandLink>
      </div>
    </Manifesto>
  );
}
export const HeartStage = styled.div`
  position: relative;
  isolation: isolate;
  min-width: 0;
  &::before {
    content: '';
    position: absolute;
    width: 72%;
    aspect-ratio: 1;
    left: 14%;
    top: 14%;
    border: 1px solid #d7d7d2;
    border-radius: 50%;
    z-index: -1;
  }
  &::after {
    content: '+';
    position: absolute;
    left: 14%;
    top: 14%;
    font: 300 2rem var(--font-mono);
    color: var(--brass);
  }
`;
